import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { initializeLocal } from "src/i18n";

import API_RESPONSE_CODE, { HTTP_STATUS_CODE } from "@constants/api.response.code";
import { getContactUsURLs } from "@constants/api.urls";
import { sessionConstants } from "@constants/appConstants";
import { USER_INFO } from "@constants/global.constants";

import { setLocalStorageItem } from "@utils/sessionUtils";

import { AccessDeniedIcon, UnauthorizedAccessIcon, UnderMaintenaceIcon, UnexpectedErrorIcon } from "@components/SVGs";
import WPButton from "@components/WPButton";
import WPHeaderFooterPage from "@components/WPHeaderFooterPage";
import WPHeading from "@components/WPHeading";

import FULL_SCREEN_ERROR_STRINGS, { ERROR_PAGE_STRINGS } from "./WPErrorFullPage.strings";

import "./WPErrorFullPage.scss";

const ERROR_PAGE_ICONS = {
  [HTTP_STATUS_CODE.SERVICE_UNAVAILABLE]: <UnderMaintenaceIcon />,
  [HTTP_STATUS_CODE.UNAUTHORIZED]: <UnauthorizedAccessIcon />,
  [HTTP_STATUS_CODE.FORBIDDEN]: <AccessDeniedIcon />,
  [API_RESPONSE_CODE.APP_CRASH]: <UnexpectedErrorIcon />,
};

const WPErrorFullPage = ({ errorData }) => {
  const { t, i18n } = useTranslation();
  const [termsLanguage, setTermsLanguage] = useState(i18n?.language ?? USER_INFO.cultureName);

  const handleContatUs = () => {
    window.open(getContactUsURLs[i18n?.language || USER_INFO.cultureName], "_blank");
  };

  const handleLoginBtnClick = () => window.location.reload();

  const { title, message } = useMemo(() => {
    const { data, status } = errorData;
    const result = FULL_SCREEN_ERROR_STRINGS(t)[status];
    if (status === HTTP_STATUS_CODE.SERVICE_UNAVAILABLE) {
      const time = data.estimatedTime || "24H";
      result.message = result.message.replace("${time}", time);
    }

    return result;
  }, [errorData, t, termsLanguage]);

  useEffect(() => {
    const updateLanguage = async () => {
      if (termsLanguage !== i18n.language) {
        setLocalStorageItem(sessionConstants.appLanguage, termsLanguage);
        await initializeLocal(termsLanguage);
      }
    };
    updateLanguage();
  }, [termsLanguage]);

  const isAppCrash = useMemo(() => errorData.status === API_RESPONSE_CODE.APP_CRASH, [errorData.status]);
  const errorStrings = useMemo(() => ERROR_PAGE_STRINGS(t), [t, termsLanguage]);
  const refreshBtnText = useMemo(() => {
    const isForbidden = errorData.status === HTTP_STATUS_CODE.UNAUTHORIZED;

    if (isForbidden) {
      return errorStrings.loginAgain;
    }

    if (isAppCrash) {
      return errorStrings.refresh;
    }

    return null;
  }, [errorData.status]);
  return (
    <WPHeaderFooterPage setTermsLanguage={setTermsLanguage} status={errorData?.status}>
      <div className='wavepro-fullpage-error-container'>
        {ERROR_PAGE_ICONS[errorData.status]}
        <WPHeading type='default'>{title}</WPHeading>
        <WPHeading className='message' secondary type='default' wrapContent={false}>
          {message}
        </WPHeading>
        <div className='button-container'>
          {!isAppCrash && (
            <WPButton id='error-contact-us-btn' variant='outlined' onClick={handleContatUs}>
              {errorStrings.contactUs}
            </WPButton>
          )}
          {refreshBtnText && (
            <WPButton id='error-login-again-btn' variant='contained' onClick={handleLoginBtnClick}>
              {refreshBtnText}
            </WPButton>
          )}
        </div>
      </div>
    </WPHeaderFooterPage>
  );
};

export default WPErrorFullPage;
